/**
 * 🥊 Crossover Showdown — Phase 2 Combat Engine
 *
 * REAL playable 1-on-1 combat. Not a storyboard. Keyboard input drives
 * a Player 1 (WASD + F/G/H attack keys) vs a simple CPU opponent. HP bars
 * respond to landed hits, K.O. detection triggers a Winner banner.
 *
 * Deliberately built with React state + rAF loop (not canvas) — same
 * side-view arena, characters positioned via CSS transforms. Optimized
 * for readability and iterative Phase 2+ expansion.
 *
 * CONTROLS (Player 1):
 *   A / D          — move left / right
 *   W              — jump
 *   Space / Shift  — block (damage reduction)
 *   F              — light punch  (6 dmg · short range)
 *   G              — heavy kick   (12 dmg · medium range)
 *   H              — special      (fighter-specific · 20 dmg)
 *
 * CPU: simple state machine — approach → attack in range → retreat on low HP.
 */
import { useEffect, useMemo, useRef, useState, useCallback, Suspense, lazy } from "react";
import { X, Play, RotateCcw, Keyboard, Box } from "lucide-react";

// Lazy-load the 3D renderer so R3F only runs when the user opts in.
const ShowdownArena3D = lazy(() => import("@/components/ShowdownArena3D"));

const ARENA_WIDTH = 800;
const ARENA_FLOOR = 60;
const FIGHTER_W = 90;
const MOVE_SPEED = 260;         // px per second
const JUMP_VELOCITY = -520;     // px/s
const GRAVITY = 1400;           // px/s²
const ATTACK_COOLDOWN = 380;    // ms
const HITSTUN = 260;            // ms — victim can't move/attack
const BLOCK_REDUCTION = 0.6;    // 60% damage reduction when blocking
const ROUND_TIME_S = 60;

const ATTACKS = {
  light:   { name: "Jab",    damage: 6,  range: 90,  duration: 200, color: "#e5e5e5" },
  heavy:   { name: "Kick",   damage: 12, range: 120, duration: 300, color: "#f59e0b" },
  special: { name: "Special", damage: 20, range: 140, duration: 420, color: "#22d3ee" },
};

const ACCENT_TO_HEX = {
  amber: "#fbbf24", emerald: "#34d399", pink: "#f472b6",
  slate: "#cbd5e1", cyan: "#22d3ee",
};

function makeFighterState(fighter, side) {
  const isLeft = side === "left";
  return {
    fighter,
    x: isLeft ? ARENA_WIDTH * 0.25 : ARENA_WIDTH * 0.75,
    y: 0,                 // vertical offset above the floor (positive = up)
    vy: 0,                // vertical velocity
    hp: 100,
    facing: isLeft ? 1 : -1,   // 1 = right, -1 = left
    onGround: true,
    attackType: null,     // 'light' | 'heavy' | 'special' | null
    attackUntil: 0,       // timestamp
    cooldownUntil: 0,
    hitstunUntil: 0,
    blocking: false,
    hitFlashUntil: 0,
    side,
  };
}

/** Draw a fighter card in its arena position. */
function FighterSprite({ f, tick }) {
  const now = performance.now();
  const isHit = now < f.hitFlashUntil;
  const isAttacking = f.attackType && now < f.attackUntil;
  const stunned = now < f.hitstunUntil;
  const rotation = isAttacking ? (f.facing === 1 ? -8 : 8) : 0;
  const scale = isAttacking ? 1.1 : (stunned ? 0.95 : 1);

  return (
    <div
      className="absolute select-none pointer-events-none"
      style={{
        left: f.x - FIGHTER_W / 2,
        bottom: ARENA_FLOOR + f.y,
        width: FIGHTER_W,
        transform: `scaleX(${f.facing}) rotate(${rotation}deg) scale(${scale})`,
        transition: "transform 120ms ease-out",
        filter: isHit ? "brightness(2) drop-shadow(0 0 20px #f43f5e)" : (f.blocking ? "brightness(0.7)" : ""),
      }}
      data-testid={`arena-fighter-${f.side}`}
      data-hp={f.hp}
    >
      <div className="text-8xl leading-none text-center" style={{ transform: `scaleX(${f.facing})` }}>
        {f.fighter.icon}
      </div>
      {/* Attack indicator */}
      {isAttacking && (
        <div
          className="absolute top-1/2 -translate-y-1/2 rounded-full"
          style={{
            left: f.facing === 1 ? FIGHTER_W - 10 : -ATTACKS[f.attackType].range + 10,
            width: ATTACKS[f.attackType].range,
            height: 24,
            background: `linear-gradient(90deg, ${ATTACKS[f.attackType].color}00, ${ATTACKS[f.attackType].color}bb, ${ATTACKS[f.attackType].color}00)`,
            transform: `scaleX(${f.facing})`,
            transformOrigin: f.facing === 1 ? "left" : "right",
            animation: "arena-attack-fade 320ms ease-out forwards",
          }}
        />
      )}
      {/* Block indicator */}
      {f.blocking && (
        <div
          className="absolute inset-0 border-2 border-white/60 rounded-full"
          style={{ animation: "arena-block-pulse 400ms ease-in-out infinite" }}
        />
      )}
    </div>
  );
}

function HpBar({ fighter, hp, align, accentHex }) {
  const pct = Math.max(0, hp);
  return (
    <div className={align === "left" ? "" : "text-right"}>
      <div className={`flex items-center ${align === "left" ? "" : "justify-end"} gap-2 mb-1`}>
        <span className="text-2xl">{fighter.icon}</span>
        <span className="text-xs uppercase tracking-widest font-bold" style={{ color: accentHex }}>
          {fighter.name}
        </span>
      </div>
      <div className={`h-4 rounded-full bg-black/60 border-2 border-neutral-800 overflow-hidden ${align === "right" ? "flex flex-row-reverse" : ""}`}>
        <div
          className="h-full transition-all duration-200"
          style={{
            width: `${pct}%`,
            background: pct > 40
              ? `linear-gradient(90deg, ${accentHex}, ${accentHex}dd)`
              : "linear-gradient(90deg, #dc2626, #f97316)",
          }}
        />
      </div>
      <div className={`text-[10px] text-neutral-500 mt-0.5 ${align === "right" ? "text-right" : ""}`}>
        {hp} / 100 HP
      </div>
    </div>
  );
}

export default function ShowdownArenaMatch({ fighter, opponent, onClose }) {
  const p2 = opponent || fighter;
  const p1Ref = useRef(makeFighterState(fighter, "left"));
  const p2Ref = useRef(makeFighterState(p2, "right"));
  const keysRef = useRef(new Set());
  const [, forceRender] = useState(0);
  const [tick, setTick] = useState(0);
  const [roundState, setRoundState] = useState("ready"); // ready | fight | ko
  const [countdown, setCountdown] = useState(3);
  const [timer, setTimer] = useState(ROUND_TIME_S);
  const [winner, setWinner] = useState(null);
  const [showHelp, setShowHelp] = useState(false);
  const [mode3d, setMode3d] = useState(false);
  const cpuStateRef = useRef({ nextDecisionAt: 0, wantsAttack: false });

  const p1AccentHex = ACCENT_TO_HEX[fighter.accent] || "#fbbf24";
  const p2AccentHex = ACCENT_TO_HEX[p2.accent] || "#f472b6";

  // Keyboard handlers
  useEffect(() => {
    const down = (e) => {
      if (e.repeat) return;
      keysRef.current.add(e.key.toLowerCase());
      // block scroll etc.
      if (["a","d","w","s","f","g","h"," "].includes(e.key.toLowerCase())) {
        e.preventDefault();
      }
    };
    const up = (e) => keysRef.current.delete(e.key.toLowerCase());
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    return () => {
      window.removeEventListener("keydown", down);
      window.removeEventListener("keyup", up);
    };
  }, []);

  // Round intro countdown
  useEffect(() => {
    if (roundState !== "ready") return;
    if (countdown <= 0) {
      setRoundState("fight");
      return;
    }
    const t = setTimeout(() => setCountdown((c) => c - 1), 800);
    return () => clearTimeout(t);
  }, [countdown, roundState]);

  // Match timer
  useEffect(() => {
    if (roundState !== "fight") return;
    const t = setInterval(() => {
      setTimer((s) => {
        if (s <= 1) {
          // Time out → higher HP wins
          const p1 = p1Ref.current, p2s = p2Ref.current;
          const w = p1.hp > p2s.hp ? "left" : (p1.hp < p2s.hp ? "right" : "draw");
          setWinner(w);
          setRoundState("ko");
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [roundState]);

  // Main game loop
  useEffect(() => {
    if (roundState !== "fight") return;
    let raf = null;
    let last = performance.now();
    const step = (now) => {
      const dt = Math.min(0.05, (now - last) / 1000); // clamp to 50ms
      last = now;
      updateFighter(p1Ref.current, p2Ref.current, dt, now, keysRef.current, /*isCpu*/ false);
      updateFighter(p2Ref.current, p1Ref.current, dt, now, keysRef.current, /*isCpu*/ true, cpuStateRef.current);
      resolveAttacks(p1Ref.current, p2Ref.current, now);
      resolveAttacks(p2Ref.current, p1Ref.current, now);
      // K.O. detection
      if (p1Ref.current.hp <= 0 || p2Ref.current.hp <= 0) {
        const w = p1Ref.current.hp <= 0 ? "right" : "left";
        setWinner(w);
        setRoundState("ko");
      }
      setTick((t) => (t + 1) % 1000000);
      forceRender((v) => v + 1);
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [roundState]);

  // Facing update — always face opponent
  useEffect(() => {
    if (roundState !== "fight") return;
    const p1 = p1Ref.current, p2 = p2Ref.current;
    p1.facing = p1.x < p2.x ? 1 : -1;
    p2.facing = p2.x < p1.x ? 1 : -1;
  }, [tick, roundState]);

  const restart = useCallback(() => {
    p1Ref.current = makeFighterState(fighter, "left");
    p2Ref.current = makeFighterState(p2, "right");
    cpuStateRef.current = { nextDecisionAt: 0, wantsAttack: false };
    setCountdown(3);
    setTimer(ROUND_TIME_S);
    setWinner(null);
    setRoundState("ready");
  }, [fighter, p2]);

  const p1 = p1Ref.current;
  const p2State = p2Ref.current;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/95 backdrop-blur-md flex items-center justify-center p-4"
      data-testid="arena-modal"
    >
      <div
        className="relative w-full max-w-5xl rounded-2xl border-2 border-neutral-700 bg-gradient-to-br from-[#0a0a0a] via-[#0f0812] to-[#0a0a0a] p-4 md:p-6 shadow-2xl"
      >
        <button
          onClick={onClose}
          data-testid="arena-close"
          className="absolute top-3 right-3 p-2 rounded-full text-neutral-300 hover:bg-white/10 hover:text-white transition z-20"
        >
          <X size={20} />
        </button>

        {/* Header · HP bars + timer */}
        <div className="grid grid-cols-3 items-center gap-4 mb-3">
          <HpBar fighter={p1.fighter} hp={p1.hp} align="left"  accentHex={p1AccentHex} />
          <div className="text-center">
            <div className="text-[10px] uppercase tracking-[0.35em] text-neutral-500">
              Crossover Showdown · Phase 2 · Live Combat
            </div>
            <div className="text-4xl font-black text-amber-300 tabular-nums" style={{ fontFamily: "Outfit, sans-serif" }}>
              {String(timer).padStart(2, "0")}
            </div>
          </div>
          <HpBar fighter={p2State.fighter} hp={p2State.hp} align="right" accentHex={p2AccentHex} />
        </div>

        {/* Arena */}
        <div
          className="relative rounded-xl border-2 border-neutral-800 bg-gradient-to-b from-[#050508] via-[#08060f] to-[#03030a] h-[380px] overflow-hidden"
          style={{ maxWidth: ARENA_WIDTH, margin: "0 auto" }}
          data-testid="arena-stage"
        >
          {/* 3D layer — occupies full arena when enabled */}
          {mode3d && (
            <Suspense fallback={
              <div className="absolute inset-0 flex items-center justify-center text-neutral-500 text-xs">
                Loading 3D fighters…
              </div>
            }>
              <div className="absolute inset-0" data-testid="arena-3d-layer">
                <ShowdownArena3D p1Ref={p1Ref} p2Ref={p2Ref} />
              </div>
            </Suspense>
          )}

          {/* 2D layer (hidden when 3D active) */}
          {!mode3d && (
            <>
              {/* stage floor */}
              <div
                className="absolute inset-x-0 bg-gradient-to-t from-neutral-900 to-transparent"
                style={{ height: ARENA_FLOOR, bottom: 0 }}
              />
              <div className="absolute inset-x-0 border-t border-neutral-800/60" style={{ bottom: ARENA_FLOOR }} />
              <FighterSprite f={p1} tick={tick} />
              <FighterSprite f={p2State} tick={tick} />
            </>
          )}

          {/* Ready countdown */}
          {roundState === "ready" && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/60 z-10">
              <div className="text-[10px] uppercase tracking-[0.35em] text-neutral-400 mb-2">Round 1</div>
              <div
                key={countdown}
                className="text-9xl font-black text-amber-300 animate-arena-countdown"
                style={{ fontFamily: "Outfit, sans-serif" }}
                data-testid="arena-countdown"
              >
                {countdown > 0 ? countdown : "FIGHT!"}
              </div>
            </div>
          )}

          {/* K.O. banner */}
          {roundState === "ko" && (
            <div
              className="absolute inset-0 flex flex-col items-center justify-center bg-black/75 backdrop-blur-sm z-10"
              data-testid="arena-ko"
            >
              <div className="text-6xl md:text-8xl font-black text-red-400 tracking-widest animate-demo-ko" style={{ fontFamily: "Outfit, sans-serif" }}>
                {winner === "draw" ? "DRAW" : "K.O."}
              </div>
              <div className="mt-3 text-xs uppercase tracking-[0.35em] text-neutral-300">
                {winner === "draw"
                  ? "Time's up — no clear victor"
                  : `${(winner === "left" ? p1.fighter : p2State.fighter).name} wins`}
              </div>
              <button
                onClick={restart}
                data-testid="arena-restart"
                className="mt-6 px-5 py-2 rounded-lg border border-neutral-600 bg-white/5 hover:bg-white/10 text-neutral-200 text-xs uppercase tracking-widest flex items-center gap-2"
              >
                <RotateCcw size={12} /> Rematch
              </button>
            </div>
          )}
        </div>

        {/* Controls */}
        <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowHelp((v) => !v)}
              data-testid="arena-help-toggle"
              className="px-3 py-1.5 rounded border border-neutral-700 bg-white/5 text-neutral-300 text-xs uppercase tracking-widest hover:bg-white/10 flex items-center gap-1.5"
            >
              <Keyboard size={12} /> Controls
            </button>
            <button
              onClick={() => setMode3d((v) => !v)}
              data-testid="arena-3d-toggle"
              className={`px-3 py-1.5 rounded border text-xs uppercase tracking-widest flex items-center gap-1.5 transition ${
                mode3d
                  ? "border-cyan-500/60 bg-cyan-500/15 text-cyan-200 shadow-[0_0_14px_rgba(34,211,238,0.35)]"
                  : "border-neutral-700 bg-white/5 text-neutral-300 hover:bg-white/10"
              }`}
              title="Toggle 3D character models (React Three Fiber)"
            >
              <Box size={12} /> {mode3d ? "3D · ON" : "3D Mode"}
            </button>
          </div>
          <div className="flex items-center gap-2">
            {roundState === "ready" && countdown > 0 && (
              <button
                onClick={() => setCountdown(0)}
                data-testid="arena-skip-intro"
                className="px-3 py-1.5 rounded border border-amber-500/50 bg-amber-500/10 text-amber-300 text-xs uppercase tracking-widest hover:bg-amber-500/20 flex items-center gap-1.5"
              >
                <Play size={12} /> Skip Intro
              </button>
            )}
            {roundState === "ko" && (
              <button
                onClick={restart}
                data-testid="arena-restart-inline"
                className="px-4 py-1.5 rounded border border-neutral-600 bg-white/5 text-neutral-200 text-xs uppercase tracking-widest hover:bg-white/10 flex items-center gap-1.5"
              >
                <RotateCcw size={12} /> Rematch
              </button>
            )}
          </div>
        </div>

        {/* Controls sheet */}
        {showHelp && (
          <div className="mt-3 rounded-lg border border-neutral-800 bg-black/60 p-3 text-[11px] text-neutral-300" data-testid="arena-help-sheet">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <ControlHint keys={["A","D"]} label="Move L/R" />
              <ControlHint keys={["W"]}    label="Jump" />
              <ControlHint keys={["Shift","Space"]} label="Block" />
              <ControlHint keys={["F"]}    label={`Jab · ${ATTACKS.light.damage} dmg`} />
              <ControlHint keys={["G"]}    label={`Kick · ${ATTACKS.heavy.damage} dmg`} />
              <ControlHint keys={["H"]}    label={`Special · ${ATTACKS.special.damage} dmg`} />
            </div>
            <div className="mt-2 text-[10px] text-neutral-500 italic">
              CPU opponent uses the mirror moveset. Block reduces incoming damage by 60%. Match ends at K.O. or after {ROUND_TIME_S}s.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ControlHint({ keys, label }) {
  return (
    <div className="flex items-center gap-2">
      <div className="flex gap-1">
        {keys.map((k) => (
          <kbd key={k} className="px-1.5 py-0.5 rounded border border-neutral-600 bg-neutral-800 text-neutral-200 text-[10px] font-mono min-w-[22px] text-center">
            {k}
          </kbd>
        ))}
      </div>
      <span className="text-neutral-400">{label}</span>
    </div>
  );
}

/* ─────────────────────── SIMULATION ─────────────────────── */

function updateFighter(self, opponent, dt, now, keys, isCpu, cpuState) {
  // Hitstun locks input
  const inHitstun = now < self.hitstunUntil;

  // Attack still in progress locks movement
  const attacking = self.attackType && now < self.attackUntil;

  // ── Inputs ──
  let moveDir = 0;
  let wantsJump = false;
  let wantsBlock = false;
  let wantsAttack = null;

  if (!isCpu && !inHitstun && !attacking) {
    if (keys.has("a")) moveDir -= 1;
    if (keys.has("d")) moveDir += 1;
    if (keys.has("w")) wantsJump = true;
    if (keys.has("shift") || keys.has(" ")) wantsBlock = true;
    if (keys.has("f")) wantsAttack = "light";
    else if (keys.has("g")) wantsAttack = "heavy";
    else if (keys.has("h")) wantsAttack = "special";
  } else if (isCpu && !inHitstun && !attacking) {
    // ── Simple CPU AI ──
    const dist = Math.abs(self.x - opponent.x);
    const facing = opponent.x > self.x ? 1 : -1;

    if (!cpuState || now > cpuState.nextDecisionAt) {
      // Re-decide behaviour every 250-500ms
      if (cpuState) {
        cpuState.nextDecisionAt = now + 250 + Math.random() * 350;
        cpuState.wantsAttack = false;
      }
      if (self.hp < 25 && Math.random() < 0.4) {
        // Defensive retreat when low HP
        moveDir = -facing;
        wantsBlock = Math.random() < 0.6;
      } else if (dist > 130) {
        moveDir = facing; // approach
      } else if (dist < 90 && Math.random() < 0.25) {
        moveDir = -facing; // spacing
      } else {
        if (cpuState) cpuState.wantsAttack = true;
      }
      if (dist > 200 && Math.random() < 0.06) wantsJump = true;
    } else {
      // Continue current attack intent
      if (dist < ATTACKS.heavy.range + 20 && cpuState && cpuState.wantsAttack) {
        const roll = Math.random();
        wantsAttack = roll < 0.55 ? "light" : roll < 0.85 ? "heavy" : "special";
        cpuState.wantsAttack = false;
      } else {
        moveDir = facing;
      }
    }
  }

  // Cooldown check
  if (wantsAttack && now < self.cooldownUntil) wantsAttack = null;

  self.blocking = wantsBlock && !attacking && !inHitstun;

  // ── Movement ──
  if (!attacking && !inHitstun) {
    self.x += moveDir * MOVE_SPEED * dt;
    // Clamp inside arena
    self.x = Math.max(FIGHTER_W / 2, Math.min(ARENA_WIDTH - FIGHTER_W / 2, self.x));
    if (wantsJump && self.onGround) {
      self.vy = JUMP_VELOCITY;
      self.onGround = false;
    }
  }

  // ── Gravity ──
  if (!self.onGround) {
    self.vy += GRAVITY * dt;
    self.y -= self.vy * dt;
    if (self.y <= 0) {
      self.y = 0;
      self.vy = 0;
      self.onGround = true;
    }
  }

  // ── Attack ──
  if (wantsAttack) {
    const a = ATTACKS[wantsAttack];
    self.attackType = wantsAttack;
    self.attackUntil = now + a.duration;
    self.cooldownUntil = now + a.duration + ATTACK_COOLDOWN;
  }

  // Clear finished attack
  if (self.attackType && now >= self.attackUntil) {
    self.attackType = null;
  }
}

function resolveAttacks(attacker, victim, now) {
  if (!attacker.attackType) return;
  const a = ATTACKS[attacker.attackType];
  // Only resolve on the first hit-frame — cache "hitLanded" per attack window
  if (attacker._lastAttackResolvedFor === attacker.attackUntil) return;
  const timeSinceStart = a.duration - (attacker.attackUntil - now);
  // Active frames: 40%..80% of attack duration
  const active = timeSinceStart >= a.duration * 0.2 && timeSinceStart <= a.duration * 0.8;
  if (!active) return;

  const dx = victim.x - attacker.x;
  const facing = attacker.facing;
  const inFront = (facing === 1 && dx > 0) || (facing === -1 && dx < 0);
  const dist = Math.abs(dx);
  if (!inFront || dist > a.range) return;

  // Land the hit
  attacker._lastAttackResolvedFor = attacker.attackUntil;
  const dmg = victim.blocking ? Math.round(a.damage * (1 - BLOCK_REDUCTION)) : a.damage;
  victim.hp = Math.max(0, victim.hp - dmg);
  victim.hitFlashUntil = now + 180;
  victim.hitstunUntil = now + (victim.blocking ? HITSTUN * 0.4 : HITSTUN);
  // Small knockback
  victim.x += facing * (victim.blocking ? 12 : 28);
  victim.x = Math.max(FIGHTER_W / 2, Math.min(ARENA_WIDTH - FIGHTER_W / 2, victim.x));
}
